import * as gasContractationActions from "../actions/gasContractation";
import FormInstallation from "../components/FormInstallation";
import FormPerson from "../components/FormPerson";
import FormGasContract from "../components/FormGasContract/gas/FormGasContract";
import FormPayment from "../components/FormPayment";
import FormConfirmation from "../components/FormConfirmation";
import Finish from "../components/Finish";
import * as collectFormDataForAPI from "../utils/collectDataAPI";
import { default as leadsService } from "../services/leads";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { 
  GasStepNames,
  StepConfirmationFormData, 
  StepInstallationFormData,
  StepPersonFormData, 
} from "../types/stepsFormData";
import { isEqual } from "lodash";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { useState } from "react";
import { Step, StepChange } from "../types/steps";
import gasContractationInitialState from "../reducers/gasContractationInitialState";

const useGasSteps = () => {
  const Settings = useSettingsContext();
  const dispatch = useAppDispatch();

  const gasContractation = useAppSelector(state => state.gasContractation);

  const [skippedSteps, setSkippedSteps] = useState(new Set<GasStepNames>());

  const findPreviousEnabledStepIdx = (currentIndex: number) => {
    const prev = -1 + currentIndex;
    if (prev < 0) {
      throw new Error(
        "Contractation out of bounds: No previous enabled step found")
    }
    if (skippedSteps.has(steps[prev].name)) {
      return findPreviousEnabledStepIdx(prev);
    } else {
      return prev;
    }
  }

  const handleGoBack = () => {
    const prevStepIdx = findPreviousEnabledStepIdx(gasContractation.currentStep.index);
    dispatch(gasContractationActions.resetLeadsErrors());
    dispatch(gasContractationActions.setLeadCurrentStep(
      prevStepIdx, steps[prevStepIdx].name
    ));
  };

  const findNextEnabledStepIdx = (
    currentIndex: number, 
    stepsToSkip: GasStepNames[]
  ) => {
    const next = 1 + currentIndex;
    if (
      next < steps.length &&
      stepsToSkip?.includes(steps[next].name)
    ) {
      return findNextEnabledStepIdx(next, stepsToSkip);
    } else {
      return next;
    }
  }

  const getCurrentSkippingSteps = (toSkipSteps: StepChange<GasStepNames>[] = []) => {
    const currentSkippedSteps = new Set(skippedSteps.values());
    toSkipSteps.forEach(step => 
      step.shouldSkip ? 
        currentSkippedSteps.add(step.name) 
      : currentSkippedSteps.delete(step.name)
    )
    return currentSkippedSteps;
  }

  const handleGoForward = (toSkipSteps?: StepChange<GasStepNames>[]) => {
    const currentSkippingSteps = getCurrentSkippingSteps(toSkipSteps);
    const followStepIdx = findNextEnabledStepIdx(
      gasContractation.currentStep.index,
      Array.from(currentSkippingSteps)
    );
    if (toSkipSteps) {
      handleChangeSkippingSteps(toSkipSteps);
    }
    if (followStepIdx) {
      dispatch(
        gasContractationActions.setLeadCurrentStep(
          followStepIdx,
          steps[followStepIdx].name
        )
      );
    }
  };

  const handleChangeSkippingSteps = (toChangeSkippingSteps: StepChange<GasStepNames>[]) => {

    toChangeSkippingSteps.forEach(stepChange => {
      if (stepChange.shouldSkip) {
        dispatch(gasContractationActions.setStepData(
          stepChange.name, 
          gasContractationInitialState["steps"][stepChange.name]))
      }
    })

    const newSkippingSteps = getCurrentSkippingSteps(toChangeSkippingSteps);
    setSkippedSteps(newSkippingSteps);
  }

  let steps: Step<GasStepNames>[] = [
    {
      name: 'installation',
      label: 'common:text.contractation_about_installation',
      content: <FormInstallation
          type="gas"
          onSubmit={ async (values: StepInstallationFormData) => {
            
            const {leadsConsent, ...checkValues} = values;
            const oldLeadsConsent = gasContractation.leadsConsent;
            if (
              !isEqual(checkValues, gasContractation.steps.installation) ||
              leadsConsent != gasContractation.leadsConsent
            ) {
              
              // If leads send to the API
              if ( Settings.features.leads && 

                  // Has given consent to store leads and receive communications
                  (values.leadsConsent || 

                    // Edge case in which the user goes back one step 
                    // and unchecks leadConsent
                    (!values.leadsConsent && oldLeadsConsent)
                  )
              ) {
                try {
                  const {lead_id: leadId} = await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectInstallationFormData(values)
                  );
                  
                  await dispatch(
                    gasContractationActions.setLeadId(leadId)
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }
              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setStepData("installation", values)
              );

            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )

            await handleGoForward();

          }}
        />
    },
    {
      name: 'person',
      label: 'common:text.contractation_about_you',
      content: <FormPerson
          type="gas"
          onSubmit={async (values: StepPersonFormData) => {
            let skipSteps: StepChange<GasStepNames>[] | undefined = undefined;
            
            if (!isEqual(values, gasContractation.steps.person)) {

              // Check if must skip any step
              if (
                Settings?.contractation?.businessHaltContractation &&
                values.company !== gasContractation.steps.person.company
              ) {
                if (values.company) {
                  skipSteps = [
                    {name: "gasContract", shouldSkip: true}, 
                    {name: "payment", shouldSkip: true}
                  ];
                } else {
                  skipSteps = [
                    {name: "gasContract", shouldSkip: false}, 
                    {name: "payment", shouldSkip: false}
                  ];
                }
              }

              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent) {

                const personFormData = collectFormDataForAPI.collectPersonFormData(
                  values,
                  gasContractation.steps.installation.cups,
                  gasContractation.steps.installation.activeContract
                )

                try {
                  await leadsService.sendGasLeadStepData(
                    personFormData
                  );
                
                  if ( Settings.contractation?.shouldCheckDNI ) {
                    const { dni_valid } = await leadsService.checkLeadDNI(gasContractation.leadId)
                    if (!dni_valid) {
                      await dispatch(gasContractationActions.setLeadsErrors({
                        dniInvalid: true
                      }));
                      
                      return;
                    }
                  };

                  if( !!Settings.contractation?.shouldCheckMorosity ) {
                    const { scoring } = await leadsService.checkLeadScore(gasContractation.leadId);
                    if (!scoring) {
                      await dispatch(gasContractationActions.setLeadsErrors({
                        badScore: true
                      }));
      
                      return;
                    }
                  }

                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setStepData("person", values)
              );

            }

            // If there is no error
            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )

            await handleGoForward(skipSteps);

          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'gasContract',
      label: 'common:text.contractation_about_gas_contract',
      content: <FormGasContract
          type="gas"
          onSubmit={async (values) => {

            if (!isEqual(values, gasContractation.steps.gasContract)) {

              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent ) {
                try {

                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectGasContractFormData(
                      values,
                      gasContractation.steps.installation.cups
                    )
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }
                
              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }

              // Register values to Redux state
              await dispatch(
                gasContractationActions.setStepData("gasContract", values)
              );

            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )
          
            await handleGoForward();

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'payment',
      label: 'common:text.contractation_about_payment',
      content: <FormPayment
          type="gas"
          onSubmit={async (values) => {

            if (!isEqual(values, gasContractation.steps.payment)) {
            
              // If leads send to the API
              if ( Settings.features.leads && gasContractation.leadsConsent ) {

                try {
                  await leadsService.sendGasLeadStepData(
                    collectFormDataForAPI.collectPaymentFormData(
                      values,
                      gasContractation.steps.installation.cups
                    )
                  );
                } catch {
                  await dispatch(gasContractationActions.setLeadsErrors({
                    genericError: true
                  }));
                  return;
                }

              }

              if (gasContractation.leadErrors.genericError) {
                await dispatch(gasContractationActions.setLeadsErrors({
                  genericError: false
                }));
              }
              
              // Register values to Redux state
              await dispatch(
                gasContractationActions.setStepData("payment", values)
              );
            }

            await dispatch(
              gasContractationActions.setLeadStepCompleted(
                gasContractation.currentStep.index
              )
            )
            
            await handleGoForward();
          
          }}

          onGoBack={handleGoBack}
        />
    },
    {
      name: 'confirmation',
      label: 'common:text.contractation_confirmation',
      content: <FormConfirmation
          type="gas"
          onSubmit={async (values: StepConfirmationFormData) => {

            dispatch(
              gasContractationActions.setStepData("confirmation", values)
            );

            try {
              
              // If lead has not been created, must send all the steps info
              if (!(Settings.features.leads && gasContractation.leadsConsent)) {
                await leadsService.sendGasLeadStepData(
                  collectFormDataForAPI.collectGasFormData(
                    gasContractation.steps
                  )
                );
              }

              let gasRes = null;
              gasRes = await leadsService.confirmGasContractation(
                collectFormDataForAPI.collectConfirmationFormData(
                  values,
                  gasContractation.steps.installation.cups
                )
              );

              if (gasRes?.status == 'OK' ) {
                await dispatch(
                  gasContractationActions.receiveConfirmNewContractResponse(
                    gasRes
                  )
                );

                await dispatch(
                  gasContractationActions.setLeadStepCompleted(
                    gasContractation.currentStep.index
                  )
                )

                await handleGoForward();

              } else {

                dispatch(
                  gasContractationActions.receiveConfirmNewContractResponse(
                    {
                      result: {
                        error: true,
                        render: {
                          format: 'markdown',
                          body: 'common:text.contractation_connectivity_error'
                        }
                      },
                      status: 'ERROR'
                    }
                  )
                );
              }
            
            } catch (err) {

              dispatch(
                gasContractationActions.receiveConfirmNewContractResponse(
                  {
                    result: {
                      error: true,
                      render: {
                        format: 'markdown',
                        body: 'common:text.contractation_connectivity_error'
                      }
                    },
                    status: 'ERROR'
                  }
                )
              );
            }

          }}
          onGoBack={handleGoBack}
        />
    },
    {
      name: 'finish',
      label: 'common:text.contractation_confirmed',
      content: <Finish
          type="gas"
          onGoBack={handleGoBack}
        />
    }
  ];

  const removeStep = (stepName: GasStepNames) => {
    steps = steps.filter(step => step.name !== stepName);
  }

  return {
    steps,
    handleGoBack
  }

}

export default useGasSteps;